/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_KEY = 'smartplan-access-key-12345';

// Create a dedicated axios instance for auth endpoints
const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Accept': 'application/json',
    'X-API-Key': API_KEY,
  },
});

export const authService = {
  /**
   * Login with username and password
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.username - Username
   * @param {string} credentials.password - Password
   * @returns {Promise<Object>} Auth tokens
   */
  async login(credentials) {
    try {
      // Ensure credentials are valid
      if (!credentials.username || !credentials.password) {
        throw new Error('Username and password are required');
      }

      // Prepare form-encoded data (OAuth2 style)
      const formData = new URLSearchParams();
      formData.append('grant_type', '');
      formData.append('username', credentials.username.trim());
      formData.append('password', credentials.password);
      formData.append('scope', '');
      formData.append('client_id', '');
      formData.append('client_secret', '');

      // Make the login request with form-encoded data
      const response = await authApi({
        method: 'POST',
        url: '/api/v1/auth/login',
        data: formData.toString(),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, refresh_token, token_type } = response.data;

      if (!access_token || !refresh_token) {
        throw new Error('Invalid response: missing tokens');
      }

      if (token_type !== 'bearer') {
        throw new Error('Invalid token type received');
      }

      // Store tokens securely
      tokenStorage.setTokens(access_token, refresh_token);

      return {
        access_token,
        refresh_token,
        token_type,
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Logout - clears stored tokens
   */
  logout() {
    tokenStorage.clearTokens();
  },

  /**
   * Refresh the access token using refresh token
   * @returns {Promise<string>} New access token
   */
  async refreshAccessToken() {
    const refreshToken = tokenStorage.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await authApi.post(
        `/api/v1/auth/refresh?token=${refreshToken}`
      );

      const { access_token } = response.data;

      if (!access_token) {
        throw new Error('Invalid refresh response: missing access token');
      }

      // Update access token while keeping refresh token
      const currentRefreshToken = tokenStorage.getRefreshToken();
      if (currentRefreshToken) {
        tokenStorage.setTokens(access_token, currentRefreshToken);
      }

      return access_token;
    } catch (error) {
      // If refresh fails, clear all tokens
      tokenStorage.clearTokens();

      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Token refresh failed';
        throw new Error(message);
      }
      throw error;
    }
  },

  /**
   * Get current access token
   * @returns {string|null} Current access token
   */
  getAccessToken() {
    return tokenStorage.getAccessToken();
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if tokens exist
   */
  isAuthenticated() {
    return tokenStorage.hasTokens();
  },
};
